import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit } from 'react-icons/fa';

const BusinessCategory = () => {
  const [businessCategories, setBusinessCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [formValues, setFormValues] = useState({
    name: '',
    categoryImage: null,
  });

  const [originalImage, setOriginalImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchBusinessCategory(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchBusinessCategory = async (page = 1, search = '') => {
    try {
      const res = await axios.get(
        `/admin/api/businessCategory?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      );

      const list =
        res.data.businessCategories ||
        (res.data.businessCategory ? [res.data.businessCategory] : []);

      setBusinessCategories(list);
      setTotalPages(Math.ceil((res.data.totalCount || list.length) / limit));
    } catch (err) {
      console.error(err);
      toastError('Failed to fetch categories');
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', formValues.name);

      if (formValues.categoryImage instanceof File) {
        formData.append('categoryImage', formValues.categoryImage);
      }

      if (editingId) {
        await axios.put(`/admin/api/businessCategory/${editingId}`, formData);
        toastSuccess('Category updated successfully');
      } else {
        await axios.post('/admin/api/businessCategory', formData);
        toastSuccess('Category added successfully');
      }

      fetchBusinessCategory(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error(err);
      toastError('Failed to save category');
    }
  };

  const handleEdit = async (category) => {
    try {
      const res = await axios.get(
        `/admin/api/businessCategory/${category._id}`
      );

      const data = res.data.businessCategory;

      setFormValues({
        name: data.name || '',
        categoryImage: null, 
      });

      setOriginalImage(data.image || null); 
      setEditingId(data._id);
      setErrors({});
      setShowModal(true);
    } catch (err) {
      console.error(err);
      toastError('Failed to load category');
    }
  };
 
  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', categoryImage: null });
    setEditingId(null);
    setErrors({});
    setOriginalImage(null);
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between mb-3">
        <h3>Business Category</h3>
        <input
          className="form-control w-25"
          placeholder="Search (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between">
          <h5 className="mb-0">Category List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Category</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {businessCategories.length ? (
                businessCategories.map((cat, i) => (
                  <tr key={cat._id}>
                    <td>{(currentPage - 1) * limit + i + 1}</td>
                    <td>
                      <img
                        src={cat.image || '/assets/images/default.png'}                           
                        alt={cat.name}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                        }}
                      />
                    </td>
                    <td>{cat.name}</td>
                    <td>
                    <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(cat)}
                      >
                        <FaEdit size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Category' : 'Add Category'}
        fields={[
          {
            name: 'categoryImage',
            label: 'Category Image',
            type: 'file',
            accept: 'image/*',
          },
          {
            name: 'name',
            label: 'Category Name',
            placeholder: 'Enter Category Name',
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        imagePreview={originalImage}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
      />
    </div>
  );
};

export default BusinessCategory;
